import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { ArrowLeft, MapPin } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useGetCustomerQuery, useGetFieldVisitsQuery } from "../customersApi";
import { paths } from "../../../../routes/paths";

// Field-visit history / "compare" report (handoff §19.3) - the full audit
// trail behind comparing the customer_master "current" pin against every
// individual scan. The current pin (from the customer's own detail
// response) is shown as a reference card up top; the table below is every
// visit, newest first, with the one that actually won the current pin
// highlighted - a scan only moves the current pin if its deviceCapturedAt
// is newer than what's already stored (§19.2), so an admin can see at a
// glance whether the current pin is fresh or driven by a stale/outlier scan.
const CustomerFieldVisitsPage = () => {
  const { branchId, custId } = useParams();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data: detail, isLoading: isLoadingCustomer } = useGetCustomerQuery({ branchId, custId });
  const { data: visits = [], isLoading: isLoadingVisits } = useGetFieldVisitsQuery({
    branchId,
    custId,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const customer = detail?.customer;
  const currentPinVisit = customer?.locationUpdatedAt
    ? visits.find((v) => dayjs(v.deviceCapturedAt).isSame(dayjs(customer.locationUpdatedAt)))
    : null;

  const columns = [
    {
      title: "Scanned At (device)",
      dataIndex: "deviceCapturedAt",
      render: (v, record) => (
        <span className={record === currentPinVisit ? "fw-bold" : ""}>
          {v ? dayjs(v).format("DD-MMM-YYYY HH:mm") : "-"}
          {record === currentPinVisit && (
            <span className="badge badge-linesuccess ms-2">Current Pin</span>
          )}
        </span>
      ),
    },
    {
      title: "Received At (server)",
      dataIndex: "receivedAt",
      render: (v) => (v ? dayjs(v).format("DD-MMM-YYYY HH:mm") : "-"),
    },
    { title: "Scanned By", dataIndex: "scannedByName", render: (v) => v || "-" },
    { title: "Module", dataIndex: "moduleContext", render: (v) => v || "-" },
    {
      title: "Coordinates",
      dataIndex: "latitude",
      render: (_, record) =>
        record.latitude != null ? `${Number(record.latitude).toFixed(6)}, ${Number(record.longitude).toFixed(6)}` : "-",
    },
    {
      title: "Accuracy",
      dataIndex: "accuracyMeters",
      render: (v) => (v != null ? `±${v}m` : "-"),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link
                to={paths.customerEdit(branchId, custId)}
                className="d-inline-flex align-items-center mb-2"
              >
                <ArrowLeft size={16} className="me-1" /> Back to {customer?.custName || "Customer"}
              </Link>
              <h4>Field-Visit History</h4>
              <h6>Every field scan for this customer, compared against the current pin</h6>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Current Location (Customer Master)</h5>
            {isLoadingCustomer ? (
              <p className="text-muted mb-0">Loading...</p>
            ) : customer?.latitude != null ? (
              <div className="d-flex flex-wrap gap-4">
                <div className="d-flex align-items-center gap-2">
                  <MapPin size={16} className="text-muted" />
                  <span>{Number(customer.latitude).toFixed(6)}, {Number(customer.longitude).toFixed(6)}</span>
                </div>
                <div className="text-muted small">
                  Updated {dayjs(customer.locationUpdatedAt).format("DD-MMM-YYYY HH:mm")}
                  {customer.locationSourceName ? ` · by ${customer.locationSourceName}` : ""}
                </div>
              </div>
            ) : (
              <p className="text-muted mb-0">No location recorded yet.</p>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="d-flex gap-2 align-items-end flex-wrap">
                <div>
                  <label className="form-label mb-1">From</label>
                  <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <label className="form-label mb-1">To</label>
                  <input type="date" className="form-control form-control-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={visits}
                rowKey={(v) => v.recordGuid || `${v.deviceCapturedAt}-${v.scannedByUserId}`}
                loading={isLoadingVisits}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerFieldVisitsPage;
